/**
 * One rendered page: a canvas that the active `PageRenderer` paints into,
 * sized so the parent slot can host absolutely-positioned overlay layers
 * in CSS-pixel document coordinates.
 *
 * Render is async; while pdf.js (or the image decoder) is working we keep
 * a placeholder of the correct intrinsic size so the surrounding scroll
 * container doesn't reflow as pages stream in.
 *
 * Concurrency: every render is admitted through a shared `RenderQueue`
 * (default cap = 2). On a large document, fast-scrolling can mount a
 * dozen canvases at once — without admission control they would all kick
 * off pdf.js renders in parallel and saturate the worker. The queue keys
 * jobs by `page.id`, so a stale render queued for the same page is
 * cancelled the moment the next one is submitted.
 */
import { memo, useEffect, useRef, useState } from 'react'

import { renderedPageSize } from './coordinates'
import { defaultRenderQueue, RenderQueueCancelled, type RenderQueue } from './renderQueue'
import type { PageModel, PageRenderer, Rotation } from './types'

export interface PageCanvasProps {
  page: PageModel
  scale: number
  rotation: Rotation
  renderer: PageRenderer
  /** Forced no-op when false — used by virtualization. */
  active: boolean
  /** Optional override; defaults to the module-scoped shared queue. */
  queue?: RenderQueue
}

interface RenderResult {
  key: string
  error: Error | null
}

function PageCanvasInner({
  page,
  scale,
  rotation,
  renderer,
  active,
  queue = defaultRenderQueue,
}: PageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // One state slot covers both "we successfully rendered key X" and "we
  // tried key X and it failed". Folding error into the same state slot
  // means the effect never needs to setError(null) synchronously — the
  // result of the next render attempt replaces both at once.
  const [result, setResult] = useState<RenderResult | null>(null)

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return

    const dpr =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1

    const key = `${page.id}|${scale.toFixed(4)}|${rotation}|${dpr}`
    if (result && result.key === key) return

    let cancelled = false

    // Drop any earlier queued (not-yet-started) render for this same page
    // slot. The new submit replaces it; in-flight work is the renderer's
    // own task to short-circuit (we ignore its resolve via `cancelled`).
    queue.cancel(page.id)

    queue
      .submit({
        key: page.id,
        task: () =>
          renderer.renderPage({
            page,
            canvas,
            scale,
            rotation,
            devicePixelRatio: dpr,
          }),
      })
      .then(() => {
        if (cancelled) {
          // A newer render was submitted after ours (effect was cleaned up).
          // pdf.js still painted to the canvas, so if the fresh render had
          // already settled, our late paint corrupted it. Reset result so the
          // current params get a clean re-render. Functional update avoids a
          // stale closure on `result`.
          setResult(prev => (prev !== null && prev.key !== key ? null : prev))
          return
        }
        setResult({ key, error: null })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // pdf.js cancellation throws a RenderingCancelledException; ignore
        // it so a stale render unmounting mid-flight doesn't surface as a
        // visible error. Same for queue-side cancellation.
        if (err instanceof RenderQueueCancelled) return
        const msg =
          err instanceof Error ? err.message : String(err ?? 'render failed')
        if (/cancel/i.test(msg)) return
        setResult({
          key,
          error: err instanceof Error ? err : new Error(msg),
        })
      })

    return () => {
      cancelled = true
      // If our submit is still queued (not yet promoted to active), drop
      // it. Without this, a page that scrolls out of view before its slot
      // opens up will still burn a shared render slot later — offscreen
      // work that competes with visible pages for the bounded queue.
      // In-flight work is unaffected (cancel() is a no-op for active jobs);
      // the `cancelled` flag above handles the late-resolve case.
      queue.cancel(page.id)
    }
  }, [active, page, scale, rotation, renderer, result, queue])

  if (result?.error) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-card text-xs text-destructive"
        data-testid="viewer-page-error"
      >
        Page failed to render: {result.error.message}
      </div>
    )
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid="viewer-page-canvas"
      data-page-number={page.pageNumber}
      className="block bg-white shadow-sm"
    />
  )
}

export const PageCanvas = memo(PageCanvasInner)

export interface PagePlaceholderProps {
  page: PageModel
  scale: number
  rotation: Rotation
}

/**
 * Reserves layout space for an un-rendered page so the virtualised scroll
 * container's scrollHeight stays correct. The placeholder is identical in
 * CSS-pixel dimensions to the rendered canvas.
 */
export function PagePlaceholder({
  page,
  scale,
  rotation,
}: PagePlaceholderProps) {
  const { width, height } = renderedPageSize(page.intrinsic, scale, rotation)
  return (
    <div
      data-testid="viewer-page-placeholder"
      data-page-number={page.pageNumber}
      className="bg-card/70 shadow-sm"
      style={{ width: `${Math.floor(width)}px`, height: `${Math.floor(height)}px` }}
    />
  )
}
